import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface LoadingConfig {
  message?: string;
  description?: string;
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
}

export interface LoadingState {
  isLoading: boolean;
  config?: LoadingConfig | null;
}

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private activeRequests = 0;

  private readonly defaultConfig: LoadingConfig = {
    message: 'Carregando dados...',
    description: 'Por favor, aguarde um momento',
    color: 'primary',
  };

  private readonly loadingStateSubject = new BehaviorSubject<LoadingState>({
    isLoading: false,
    config: null,
  });

  /**
   * Observable que indica o estado completo de loading
   */
  public readonly loadingState$: Observable<LoadingState> =
    this.loadingStateSubject.asObservable();

  /**
   * Observable que indica se há requisições em andamento (compatibilidade)
   */
  public readonly loading$: Observable<boolean> = this.loadingStateSubject
    .asObservable()
    .pipe(map((state) => state.isLoading));

  /**
   * Obtém o estado atual de loading
   */
  public get isLoading(): boolean {
    return this.loadingStateSubject.value.isLoading;
  }

  /**
   * Incrementa o contador de requisições ativas e mostra o loading
   */
  public show(config?: LoadingConfig): void {
    this.activeRequests++;
    this.updateLoadingState(config);
  }

  /**
   * Decrementa o contador de requisições ativas e esconde o loading se necessário
   */
  public hide(): void {
    if (this.activeRequests > 0) {
      this.activeRequests--;
    }
    this.updateLoadingState();
  }

  /**
   * Força o reset do loading (útil em casos de erro)
   */
  public reset(): void {
    this.activeRequests = 0;
    this.updateLoadingState();
  }

  /**
   * Atualiza o estado do loading baseado no número de requisições ativas
   */
  private updateLoadingState(config?: LoadingConfig): void {
    const shouldShow = this.activeRequests > 0;
    const currentState = this.loadingStateSubject.value;

    let finalConfig: LoadingConfig | null = null;

    if (shouldShow) {
      // Se deve mostrar loading, usa config fornecido ou o padrão
      finalConfig = config
        ? { ...this.defaultConfig, ...config }
        : this.defaultConfig;
    }

    const newState: LoadingState = {
      isLoading: shouldShow,
      config: finalConfig,
    };

    if (currentState.isLoading !== shouldShow || (shouldShow && config)) {
      this.loadingStateSubject.next(newState);
    }
  }
}
